/**
 * Format amount properly for blockchain transactions
 * @param amount Amount to format
 * @returns Formatted amount as string
 */
export const formatAmount = (amount: string | number): string => {
  // Convert to string if it's a number
  let formattedAmount = amount.toString();

  // Remove any commas that might be in the amount
  formattedAmount = formattedAmount.replace(/,/g, '');

  // Handle scientific notation (e.g., 1e-8)
  if (formattedAmount.includes('e')) {
    const num = Number(formattedAmount);
    formattedAmount = num.toFixed(18).replace(/\.?0+$/, '');
  }

  return formattedAmount;
};

/**
 * Validate if an amount is a valid number
 * @param amount Amount to validate
 * @returns True if valid, false otherwise
 */
export const isValidAmount = (amount: string | number): boolean => {
  const formattedAmount = formatAmount(amount);
  return !isNaN(Number(formattedAmount));
};

/**
 * Get estimated time based on selected speed
 * @param speed Transaction speed
 * @returns Estimated time string
 */
export const getEstimatedTime = (speed: 'slow' | 'medium' | 'fast'): string => {
  switch (speed) {
    case 'slow':
      return '~30 minutes';
    case 'medium':
      return '~10 minutes';
    case 'fast':
      return '~2 minutes';
    default:
      return '~10 minutes';
  }
};

/**
 * Check if fee estimation should be updated based on inputs
 * @param currentInputs Current form inputs
 * @param lastInputs Last inputs used for estimation
 * @returns True if estimation should be updated
 */
export const shouldUpdateFeeEstimation = (
  currentInputs: {
    amount: string;
    to_address: string;
    token: string;
    speed: 'slow' | 'medium' | 'fast';
  },
  lastInputs: {
    amount: string;
    to_address: string;
    token: string;
    speed: 'slow' | 'medium' | 'fast';
    timestamp: number;
  }
): boolean => {
  // If any of the inputs have changed, update the estimation
  return (
    currentInputs.amount !== lastInputs.amount ||
    currentInputs.to_address !== lastInputs.to_address ||
    currentInputs.token !== lastInputs.token ||
    currentInputs.speed !== lastInputs.speed ||
    // Or if it's been more than 30 seconds since the last estimation
    Date.now() - lastInputs.timestamp > 30000
  );
};
